/**
 * Fetch Initial Data Actor
 *
 * XState actor that fetches the import preview data.
 * Returns the data along with status so the machine can decide
 * whether to wait for a running process.
 */
import { fromPromise } from 'xstate'
import { fetchImportPreviewData } from 'services/importengine'
import {
  ImportData,
  ImportDataRowsFormat,
  ProcessRunning,
} from '../../importer-types'

export interface FetchInitialDataInput {
  importNumber: number
}

export interface FetchInitialDataOutput {
  data: ImportData | null
  skipMatching: boolean
  status: 'idle' | 'running'
  processRunning: ProcessRunning | null
  importName: string
}

/**
 * Convert ImportDataRowsFormat (from API) to ImportData (for UI)
 */
function fromRowsJSON(data: ImportDataRowsFormat): ImportData {
  return {
    headers: data.headers.map((h) => ({
      original: h.original,
      mapped: h.mapped,
    })),
    rows:
      data.rows?.rows?.map(
        (row) =>
          Object.fromEntries(
            (data.rows?.columns || []).map((key, i) => [key, row[i]])
          ) as ImportData['rows'][number]
      ) || [],
  }
}

export const fetchInitialDataActor = fromPromise<
  FetchInitialDataOutput,
  FetchInitialDataInput
>(async ({ input }) => {
  const response = await fetchImportPreviewData(input.importNumber)

  return {
    data: response.data ? fromRowsJSON(response.data) : null,
    skipMatching: response.skip_matching ?? false,
    status: response.status ?? 'idle',
    processRunning: response.process_running ?? null,
    importName: response.name ?? '',
  }
})
